import { findReplacementTx } from 'find-replacement-tx'

import { CHAIN_INFO } from 'constants/chains'
import { numbers } from 'constants/variables'

import { Options, Transaction, TransactionByHash, TransactionResult, WaitForTxReceiptParams } from './types'
import { ExtendedProvider } from './ExtendedProvider'

const DEFAULT_ATTEMPTS = 1
const MIN_CONFIRMATIONS = 1
export class Provider {
  public provider: ExtendedProvider
  private readonly chainId: SupportedChainId

  public constructor(options: Options) {
    this.provider = new ExtendedProvider(options.url, options.chainId)
    this.chainId = options.chainId
  }

  public async waitForTxReceipt({ txHash }: WaitForTxReceiptParams): Promise<Transaction> {
    try {
      const multiplier = 10
      const callRetryAttempt = 15
      const totalAttempt = callRetryAttempt * multiplier

      const getTransactionByHash = async () => {
        return await this.provider.getTransaction(txHash)
      }

      const startBlock = (await this.provider.getBlockNumber()) - 500

      const transaction = await this.repeatRequestUntilResult<TransactionByHash>(getTransactionByHash, totalAttempt)
      const findTransactionReceipt = async () => {
        let receipt = await this.provider.getTransactionReceipt(txHash)

        if (!receipt) {
          const tx = {
            nonce: transaction.nonce,
            from: transaction.from,
            to: transaction.to,
            data: transaction.data,
          }
          const foundTx = await findReplacementTx(this.provider, startBlock, tx)

          if (foundTx) {
            receipt = await this.provider.getTransactionReceipt(foundTx.hash)
          }
        }

        return receipt
      }
      const receipt = await this.repeatRequestUntilResult<TransactionResult>(findTransactionReceipt, totalAttempt)

      return Object.assign(receipt, { value: transaction.value })
    } catch (err) {
      throw new Error(`Provider method waitForTxReceipt has error: ${err.message}`)
    }
  }

  public async waitForTxConfirmations({
    txHash,
    callback,
    minConfirmation = MIN_CONFIRMATIONS,
    attempt = DEFAULT_ATTEMPTS,
  }: WaitForTxReceiptParams): Promise<boolean> {
    try {
      const callRetryAttempt = 150

      if (attempt === DEFAULT_ATTEMPTS) {
        const txResponse = await this.provider.getTransaction(txHash)

        await txResponse.wait(MIN_CONFIRMATIONS)
      }

      const receipt = await this.provider.getTransactionReceipt(txHash)

      if (typeof callback === 'function' && receipt) {
        callback(receipt)
      }

      if (receipt?.confirmations >= minConfirmation) {
        return true
      } else if (attempt >= callRetryAttempt) {
        return false
      } else {
        attempt++
        await this.sleep(CHAIN_INFO[this.chainId].blockDuration)
        return await this.waitForTxConfirmations({ txHash, minConfirmation, attempt, callback })
      }
    } catch (err) {
      attempt++
      await this.sleep(CHAIN_INFO[this.chainId].blockDuration)
      return await this.waitForTxConfirmations({ txHash, minConfirmation, attempt, callback })
    }
  }

  private async sleep(ms: number) {
    return await new Promise((resolve) => setTimeout(resolve, ms))
  }

  private async repeatRequestUntilResult<T>(
    action: CallableFunction,
    totalAttempts: number,
    retryAttempt: number = DEFAULT_ATTEMPTS,
  ): Promise<T> {
    return await new Promise((resolve, reject) => {
      const iteration = async (): Promise<void> => {
        try {
          const result = await action()
          if (!result) {
            throw new Error('Tx not minted')
          } else {
            resolve(result)
          }
        } catch (err) {
          if (retryAttempt <= totalAttempts) {
            retryAttempt++
            setTimeout(() => {
              // eslint-disable-next-line no-void
              void iteration()
            }, numbers.SECOND * retryAttempt)
          } else {
            return reject(err)
          }
        }
      }

      // eslint-disable-next-line no-void
      void iteration()
    })
  }
}

class Providers {
  private readonly providers: Map<string, Provider> = new Map()

  public getProvider(chainId: SupportedChainId, rpcUrl: string): Provider {
    const key = `${chainId}-${rpcUrl}`
    if (!this.providers.has(key)) {
      this.providers.set(key, new Provider({ url: rpcUrl, chainId }))
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.providers.get(key)!
  }
}

const providers = new Providers()

export function getProvider(chainId: SupportedChainId, rpcUrl: string): Provider {
  return providers.getProvider(chainId, rpcUrl)
}
